import React, { useRef } from 'react'
import { Alert, Avatar, Button, Card, Checkbox, Form, Input, Modal, Radio, Row, Spin } from 'antd'
import { Link } from 'react-router-dom'
import { UserOutlined } from '@ant-design/icons'
import TermsAndConditions from './TermsAndConditions'
import {
  AMC,
  COMPANY,
  COMPANY_MEMBER,
  COMPANY_MANAGER,
  CONTRACT_APPRAISER,
  PROPERTY_DATA_COLLECTOR,
  SOLE_PROPRIETOR,
  STAFF_APPRAISER,
} from '@components/Signup/constants'
import PasswordRequirements from '@reggora_shared_ui/components/PasswordRequirements'
import PropertyDataCollectorSetupContainer from '../PropertyDataCollectorSetup/PropertyDataCollectorSetupContainer'

import '@components/Platform/Shared/Setup/AccountSetup.scss'

const userTypeTitleMap = {
  [COMPANY]: 'Basic Company Info',
  [COMPANY_MEMBER]: 'Basic Info',
  [SOLE_PROPRIETOR]: 'Basic Company Info',
  [AMC]: 'Basic Company Info',
}

const labelCol = { span: 24 }

const CreateAccount = ({
  userType,
  initialValues,
  invitedBy: { invitee, invitationReason } = {},
  submitting,
  isAppraiser,
  termsModalVisible,
  agreed,
  passwordRequirementsMet,
  validationErrors = [],
  formValues,
  singlePageFormCompleting,
  onSubmit,
  onSinglePageFormSubmit,
  onAgree,
  onDisagree,
  onOpenTermsModal,
  onPasswordRequirementsChange,
  onFormValuesChange,
  onAppraiserChange,
  onClearValidationErrors,
  onSetTermsAgreed,
  validatePassword,
  shellUser,
}) => {
  const formRef = useRef(null)

  const isAMC = userType === AMC
  const isCompany = userType === COMPANY
  const isSoleProprietor = userType === SOLE_PROPRIETOR
  const isCompanyMember = userType === COMPANY_MEMBER
  const isPropertyDataCollector = userType === PROPERTY_DATA_COLLECTOR
  const isAdmin = isCompany || isSoleProprietor || isAMC

  const handleFormValuesChange = (changedValues, allValues) => {
    onFormValuesChange(changedValues, allValues, formRef)
  }

  // Error banner/alert component
  const renderErrorSummary = () => {
    if (validationErrors.length === 0) {
      return null
    }

    return (
      <Alert
        message={`Please correct the following ${validationErrors.length > 1 ? 'errors' : 'error'}:`}
        description={
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
            {validationErrors.map((error, index) => (
              <li key={index} style={{ marginBottom: '4px' }}>
                {error.message}
              </li>
            ))}
          </ul>
        }
        type="error"
        showIcon
        style={{ marginBottom: '20px' }}
        closable
        onClose={onClearValidationErrors}
      />
    )
  }

  // Reusable form components
  const renderCompanyNameField = () => (
    <Form.Item
      label="Company Name"
      labelCol={labelCol}
      name="company_name"
      rules={[
        {
          required: true,
          message: 'Please enter your company name',
        },
      ]}
    >
      <Input size="large" data-testid="signup-company-name-input" />
    </Form.Item>
  )

  const renderNameFields = () => (
    <>
      <Form.Item
        label={`${isAdmin ? 'Admin ' : ''}First Name`}
        labelCol={labelCol}
        name="firstname"
        rules={[
          {
            required: true,
            message: 'Please enter your first name',
          },
        ]}
      >
        <Input placeholder="First Name" size="large" data-testid="signup-first-name-input" />
      </Form.Item>
      <Form.Item
        label={`${isAdmin ? 'Admin ' : ''}Last Name`}
        labelCol={labelCol}
        name="lastname"
        rules={[
          {
            required: true,
            message: 'Please enter your last name',
          },
        ]}
      >
        <Input placeholder="Last Name" size="large" data-testid="signup-last-name-input" />
      </Form.Item>
    </>
  )

  const renderEmailField = () => (
    <Form.Item
      label="Email"
      labelCol={labelCol}
      name="email"
      rules={[
        {
          required: true,
          message: 'Please enter your email',
          type: 'email',
        },
      ]}
    >
      <Input type="email" disabled={true} size="large" data-testid="signup-email-input" />
    </Form.Item>
  )

  const renderPasswordFields = () => (
    <>
      <Form.Item
        label="Password"
        labelCol={labelCol}
        name="password"
        rules={[
          {
            min: 8,
            required: true,
            message: 'Password must be at least 8 characters',
          },
          () => ({
            validator: validatePassword,
          }),
        ]}
      >
        <Input placeholder="Password" type="password" size="large" data-testid="signup-password-input" />
      </Form.Item>
      <Form.Item
        label="Confirm Password"
        labelCol={labelCol}
        name="confirm_password"
        rules={[
          {
            required: true,
            message: 'Please confirm your password',
          },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('password') === value) {
                return Promise.resolve()
              }
              return Promise.reject(new Error('The two passwords that you entered do not match'))
            },
          }),
        ]}
      >
        <Input placeholder="Confirm Password" type="password" size="large" data-testid="signup-confirm-password-input" />
      </Form.Item>
      <Form.Item
        name="password_requirements"
        rules={[
          () => ({
            validator() {
              if (passwordRequirementsMet) {
                return Promise.resolve()
              }
              return Promise.reject(new Error('Please meet all password requirements'))
            },
          }),
        ]}
      >
        <PasswordRequirements
          password={formValues.password}
          confirmPassword={formValues.confirm_password}
          onRequirementsChange={onPasswordRequirementsChange}
        />
      </Form.Item>
    </>
  )

  const renderAppraiserQuestionField = () => (
    <Form.Item
      label="Are you an appraiser?"
      labelCol={labelCol}
      name="is_appraiser"
      rules={[
        {
          required: true,
          message: 'This question is required.',
        },
      ]}
    >
      <Radio.Group name="is_appraiser" onChange={(e) => onAppraiserChange(e.target.value)} value={isAppraiser} data-testid="signup-is-appraiser-radio">
        <Row>
          <Radio value={true}>Yes</Radio>
        </Row>
        <Row>
          <Radio value={false}>No</Radio>
        </Row>
      </Radio.Group>
    </Form.Item>
  )

  const renderAppraiserTypeField = () => (
    <Form.Item
      label="Are you a staff appraiser or contract appraiser?"
      labelCol={labelCol}
      name="appraiser_type"
      rules={[
        {
          required: true,
          message: 'This question is required.',
        },
      ]}
    >
      <Radio.Group name="appraiser_type" data-testid="signup-appraiser-type-radio">
        <Row>
          <Radio value={STAFF_APPRAISER}>Staff, I use a W2 on my tax information</Radio>
        </Row>
        <Row>
          <Radio value={CONTRACT_APPRAISER}>Contract, I use a W9 on my tax information</Radio>
        </Row>
      </Radio.Group>
    </Form.Item>
  )

  const renderTermsAndConditionsField = () => (
    <Form.Item
      className="required-checkbox"
      label="Terms and Conditions"
      labelCol={labelCol}
      name="terms_and_conditions"
      rules={[
        () => ({
          validator() {
            if (agreed) {
              return Promise.resolve()
            }
            return Promise.reject(new Error('You must agree to the terms and conditions.'))
          },
        }),
      ]}
    >
      <Checkbox
        name="terms_and_conditions"
        onChange={(e) => onSetTermsAgreed(e.target.checked)}
        checked={agreed}
        value={true}
        data-testid="signup-terms-checkbox"
      >
        <span>I agree to the&nbsp;</span>
        <Button type="link" onClick={onOpenTermsModal} style={{ paddingLeft: 0 }}>
          Terms and Conditions
        </Button>
      </Checkbox>
    </Form.Item>
  )

  // Main render functions for each user type
  const renderFieldsForUserType = () => {
    const commonFields = (
      <>
        {renderNameFields()}
        {renderEmailField()}
        {renderPasswordFields()}
      </>
    )

    switch (userType) {
      case COMPANY:
        return (
          <>
            {renderCompanyNameField()}
            {commonFields}
            {renderAppraiserQuestionField()}
            {!isSoleProprietor && (isAppraiser || isCompanyMember) && renderAppraiserTypeField()}
            {renderTermsAndConditionsField()}
          </>
        )

      case COMPANY_MANAGER:
        return (
          <>
            {commonFields}
            {renderAppraiserQuestionField()}
            {!isSoleProprietor && (isAppraiser || isCompanyMember) && renderAppraiserTypeField()}
          </>
        )

      case COMPANY_MEMBER:
        return (
          <>
            {commonFields}
            {!isSoleProprietor && (isAppraiser || isCompanyMember) && renderAppraiserTypeField()}
          </>
        )

      case SOLE_PROPRIETOR:
        return (
          <>
            {renderCompanyNameField()}
            {commonFields}
            {renderTermsAndConditionsField()}
          </>
        )

      case AMC:
        return (
          <>
            {renderCompanyNameField()}
            {commonFields}
            {renderTermsAndConditionsField()}
          </>
        )

      default:
        return commonFields
    }
  }

  const renderInvitationCard = () => {
    if (!invitee || !invitationReason) {
      return null
    }

    return (
      <Card style={{ marginTop: '5px' }}>
        <div className="signup-invitation">
          <div className="invitation-contents">
            <Avatar size="large" icon={<UserOutlined />} />
            <div className="invitation-details">
              <p className="invitee">{invitee}</p>
              <small className="invitee-reason">{invitationReason}</small>
            </div>
          </div>
        </div>
      </Card>
    )
  }

  // Render PDC users using the container
  const renderPDCForm = () => {
    return (
      <div>
        {singlePageFormCompleting
          ? (
          <Spin tip="Completing setup..." spinning={true} size="large" className="full-page-spinner" />
            )
          : (
          <>
            <div className="step-header">
              <h1>Create Account</h1>
              <p className="center-text">
                Already have an account? <Link to="/login">Log In</Link>
              </p>
            </div>
            <PropertyDataCollectorSetupContainer
              handleSinglePageFormSubmit={onSinglePageFormSubmit}
              initialValues={initialValues}
              validationErrors={validationErrors}
              onClearValidationErrors={onClearValidationErrors}
              submitting={submitting}
              shellUser={shellUser}
            />
          </>
            )}
      </div>
    )
  }

  // Conditionally render PDC form or regular form
  return isPropertyDataCollector
    ? renderPDCForm()
    : (
        <div>
          <div className="step-header">
            <h1>Create Account</h1>
            <p className="center-text">
              Already have an account? <Link to="/login">Log In</Link>
            </p>
          </div>
          <Card>
            <h3>{userTypeTitleMap[userType]}</h3>

            {/* Error banner/alert displayed at top of form */}
            {renderErrorSummary()}

            <div className="modern-signup-form">
              <Form
                ref={formRef}
                initialValues={initialValues}
                validateTrigger="onSubmit"
                onValuesChange={handleFormValuesChange}
                layout="vertical"
                data-testid="signup-create-account-form"
              >
                {renderFieldsForUserType()}
                <div className="controls">
                  <Button className="submit-button" type="primary" disabled={submitting} onClick={() => onSubmit(formRef)} data-testid="signup-submit-button">
                    {submitting ? <Spin /> : 'Create Account'}
                  </Button>
                </div>
              </Form>
            </div>
          </Card>
          {renderInvitationCard()}
          <Modal
            className="terms-modal"
            title="Terms and Conditions"
            onOk={onAgree}
            onCancel={onDisagree}
            visible={termsModalVisible}
            footer={[
              <Button key="disagree" onClick={onDisagree}>
                Disagree
              </Button>,
              <Button key="agree" type="primary" onClick={onAgree}>
                Agree
              </Button>,
            ]}
          >
            <TermsAndConditions />
          </Modal>
        </div>
      )
}

export default CreateAccount
